import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { EASE, FadeUp, Eyebrow, MaskedLines } from "./Reveal";

const FAQS = [
  {
    id: 1,
    q: "How much does a website cost?",
    a: "Every website is quoted on a quick call — priced to your scope, not squeezed into a template. You'll get a straight number before anything starts, and optional ongoing management is available if you want updates handled for you. Want a fixed deal? The Growth Package bundle has set prices.",
  },
  {
    id: 2,
    q: "What's the difference between Ad Launch and the Growth Package?",
    a: "Both are the same deal at heart: one setup fee, then 10% of what you earn from jobs won through the ads — nothing monthly, ever. Ad Launch is ads only with a $550 AUD setup; the Growth Package adds your website (quoted on the call) with a discounted $450 AUD setup, and your positioning and direction handled.",
  },
  {
    id: 3,
    q: "Who pays for the Meta ad spend?",
    a: "You do — paid directly to Meta by you. My fee covers management, optimisation, testing and strategy only, so every dollar of budget goes straight into your ads.",
  },
  {
    id: 4,
    q: "Is there a lock-in contract?",
    a: "No lock-in. Meta Ads management runs month to month — you stay because the results are there, not because of paperwork.",
  },
  {
    id: 5,
    q: "What do you need from me to start?",
    a: "Just a quick call. For Meta Ads, access to your Meta Business Manager — or we'll set one up for you. For websites, your logo, photos and any copy you already have; we handle the rest.",
  },
  {
    id: 6,
    q: "How long does a website take to launch?",
    a: "Every project is scoped on the first call and you'll get a clear timeline before anything starts. The process is lean, so builds move from kickoff to launch quickly — not months of back and forth.",
  },
  {
    id: 7,
    q: "What have you built before?",
    a: "Recent builds are live in the Work section above — CRF Gardening & Maintenance and AP Pumping. Every project gets the same treatment: designed, built and shipped end to end.",
  },
  {
    id: 8,
    q: "Do you only work with local businesses?",
    a: "No — everything is handled remotely, so it doesn't matter where you're based. If you have customers, we can reach them.",
  },
];

function Item({ faq, open, onToggle }) {
  return (
    <div className="border-b border-white/[0.06]">
      <button
        data-testid={`faq-q-${faq.id}`}
        onClick={onToggle}
        className="group flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span
          className={`font-display text-lg font-light tracking-tight transition-colors duration-300 sm:text-xl ${
            open ? "text-[#E5C158]" : "text-white group-hover:text-[#E5C158]/80"
          }`}
        >
          {faq.q}
        </span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.35, ease: EASE }} className="shrink-0">
          <Plus className="h-4 w-4 text-[#D4AF37]" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-6 text-sm leading-relaxed text-neutral-400">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(1);

  return (
    <section id="faq" data-testid="faq-section" className="relative border-t border-white/[0.06] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Eyebrow className="mb-8">04 — FAQ</Eyebrow>
            <MaskedLines
              inView
              as="h2"
              className="font-display text-2xl font-light leading-tight tracking-tight sm:text-4xl"
              lines={[
                { content: "Questions," },
                { content: <span className="italic text-[#E5C158]">answered.</span> },
              ]}
            />
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-xs text-sm leading-relaxed text-neutral-400">
                Anything else on your mind? Call, email, or send an inquiry
                below — happy to talk it through.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.1}>
              <div data-testid="faq-list" className="border-t border-white/[0.06]">
                {FAQS.map((f) => (
                  <Item key={f.id} faq={f} open={open === f.id} onToggle={() => setOpen(open === f.id ? null : f.id)} />
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}